import { cn } from 'cn';
import { ChevronsUpDown, LogOut, MonitorSmartphone, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useLogout } from '@/api/queries/auth';
import { roleLabel } from '@/auth/permissions';
import { useSession } from '@/auth/use-session';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { initials } from '@/lib/initials';

/**
 * The account menu. `sidebar`: a card at the bottom of the sidebar (name + email); `reveal`
 * fades the text while the sidebar is collapsed, and `collapsed` drops the card's frame so the
 * avatar lines up with the menu icons. `topbar`: just the avatar (phones, tablets).
 */
export function UserMenu({
  variant = 'topbar',
  reveal = '',
  collapsed = false,
  admin = false,
}: {
  variant?: 'topbar' | 'sidebar';
  reveal?: string;
  collapsed?: boolean;
  /** In the Super Admin panel: its own account pages, and the staff role instead of a company role. */
  admin?: boolean;
}) {
  const session = useSession();
  const { user, role } = session;
  const logout = useLogout();
  const navigate = useNavigate();

  const signOut = (everywhere: boolean) =>
    logout.mutate(everywhere, {
      onSettled: () => {
        toast.success(everywhere ? 'Logged out of all devices' : 'Logged out');
        void navigate('/login', { replace: true });
      },
    });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'sidebar' ? (
          <button
            type="button"
            aria-label={`Account menu for ${user.name}`}
            className={cn(
              'flex w-full items-center gap-3 rounded-xl border p-1.5 text-left transition-[background-color,border-color,box-shadow] duration-150 hover:bg-muted/60',
              collapsed ? 'border-transparent bg-transparent shadow-none' : 'bg-card shadow-xs',
            )}
          >
            <Avatar className="size-9 shrink-0">
              <AvatarFallback className="bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-semibold text-white">
                {initials(user.name)}
              </AvatarFallback>
            </Avatar>
            <span className={cn('min-w-0 flex-1', reveal)}>
              <span className="block truncate text-sm font-semibold">{user.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
            </span>
            <ChevronsUpDown
              aria-hidden="true"
              className={cn('size-4 shrink-0 text-muted-foreground', reveal)}
            />
          </button>
        ) : (
          <Button
            variant="ghost"
            className="h-9 gap-2 px-1.5"
            aria-label={`Account menu for ${user.name}`}
          >
            <Avatar className="size-7">
              <AvatarFallback className="bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-semibold text-white">
                {initials(user.name)}
              </AvatarFallback>
            </Avatar>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === 'sidebar' ? 'start' : 'end'}
        side={variant === 'sidebar' ? 'top' : 'bottom'}
        className="w-64"
      >
        <DropdownMenuLabel className="grid gap-0.5">
          <span className="truncate">{user.name}</span>
          <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
          {admin ? (
            <span className="text-xs font-normal text-muted-foreground">
              {session.platformRole === 'super_admin' ? 'Super Admin' : 'Tekansa Support'}
            </span>
          ) : (
            role && (
              <span className="text-xs font-normal text-muted-foreground">
                {session.roleName ?? roleLabel(role)}
              </span>
            )
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            void navigate(admin ? '/admin/account/profile' : '/settings/account/profile')
          }
        >
          <UserRound aria-hidden="true" /> Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => signOut(false)} disabled={logout.isPending}>
          <LogOut aria-hidden="true" /> Log out
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => signOut(true)} disabled={logout.isPending}>
          <MonitorSmartphone aria-hidden="true" /> Log out of all devices
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
